import React, { useContext, useEffect, useState, useCallback } from 'react'
import {
  BodyText,
  Accordion,
  AccordionRow,
  Card,
  LockIcon,
  Radio,
  Divider,
  CaptionText,
  HeadingText,
  CalendarIcon,
} from '@paypalcorp/pp-react'
import { toCommaSeparatedNumber } from '../__utils__.js'
import { financialCalculatorService } from '../../services/financialCalculator.service'
import { OfferInfoMetricsV2 } from './offerInfoV2'
import { OfferContext } from './offerContext'

import {
  getCurrencyFormat,
  getLocalizedText,
} from 'ppbl/utils/locale-helper.js'
import { interpolate } from 'ppbl/utils/string-helper'
import clientLogger from 'ppbl/utils/client-logger'
import { isEmpty } from 'lodash-es'

type Props = {
  loanTermLable: string
  loanTermValue: string
  handleSelection: (...args: any[]) => any
  loanAmount: number
  loanLimit: number
  isSelected: boolean
  ind: number
}

const TermInputRadioV2: React.FC<Props> = ({
  loanTermLable,
  loanTermValue,
  handleSelection,
  loanAmount,
  loanLimit,
  isSelected,
  ind,
}) => {
  const {
    disableContinue,
    renewalStatus,
    customOfferConstraints,
    previousLoans,
    offerMinAmount,
  } = useContext(OfferContext)

  function formattedCurrency(value) {
    return getCurrencyFormat(Number(value))
  }

  const [isEnabled, setIsEnabled] = useState(loanAmount <= loanLimit)
  const [offerValues, setOfferValues] = useState<any>({})

  const radioButtonFinancialCalculator = (desiredAmount) => {
    if (!desiredAmount) {
      clientLogger.warn(
        `desiredAmount is undefined or null - RadioButton-V2-${loanTermValue}`,
      )
      return {}
    }
    const values: any = {}
    const offers = customOfferConstraints
    values.offerAmount = desiredAmount
    values.termInWeeks = Number(loanTermValue)

    values.weeklyRate = financialCalculatorService.getWeeklyPriceByOffer(
      values.termInWeeks,
      offers,
    )
    values.weeklyPayment = financialCalculatorService.getWeeklyTotalPayment(
      values.offerAmount,
      values.termInWeeks,
      values.weeklyRate,
    )
    values.financeCharges = financialCalculatorService.getTotalFinanceCharges(
      values.offerAmount,
      values.termInWeeks,
      values.weeklyRate,
    )

    return values
  }
  radioButtonFinancialCalculator(Number(loanAmount))

  // memoize the calculate offer value function
  const calculateOfferMetrics = useCallback(radioButtonFinancialCalculator, [
    customOfferConstraints,
    loanTermValue,
  ])

  useEffect(() => {
    setIsEnabled(loanAmount >= offerMinAmount && loanAmount <= loanLimit)
    if (isSelected) disableContinue(!isEnabled)
  }, [
    isSelected,
    isEnabled,
    loanAmount,
    loanLimit,
    disableContinue,
    offerMinAmount,
  ])

  useEffect(() => {
    let computedValues = calculateOfferMetrics(Number(loanAmount))
    setOfferValues(computedValues)
  }, [calculateOfferMetrics, loanAmount])

  return (
    <div className="radioCardContainerV2">
      <Card
        className={`radioCardWrapperV2 ${
          isSelected
            ? 'radioCardBorderSelectedV2'
            : 'radioCardBorderUnselectedV2'
        }`}
        data-dd-privacy="mask"
      >
        {isEnabled ? (
          <Accordion>
            <AccordionRow
              isOpen={isSelected}
              heading={
                <Radio
                  type="radio"
                  id={`offer-radio-${ind}`}
                  name="selectedOffer"
                  value={loanTermValue}
                  onChange={handleSelection}
                  checked={isSelected}
                  className="radioButtonV2"
                  label={
                    <div
                      className="radioLabelV2"
                      id={`offer-term-card-${loanTermValue}`}
                    >
                      <div className="radioLabelLeftV2">
                        <div className="radioLabelValueV2">
                          <HeadingText size="title">
                            {offerValues?.weeklyPayment &&
                              formattedCurrency(offerValues.weeklyPayment)}
                          </HeadingText>
                        </div>
                        <CaptionText>
                          {getLocalizedText(
                            'offer-page.termInputRadio.radioOptionLeftTextOpt2',
                          )}
                        </CaptionText>
                      </div>
                      <div className="radioLabelRightV2">
                        <CalendarIcon size="xs" color="sysColorPrimaryMain" />
                        <HeadingText size="title">{loanTermValue}</HeadingText>
                        <CaptionText>
                          {getLocalizedText(
                            'offer-page.termInputRadio.radioOptionRightTextOpt2',
                          )}
                        </CaptionText>
                      </div>
                    </div>
                  }
                />
              }
            >
              <div className="radioInfoSectionV2">
                <OfferInfoMetricsV2
                  infoLabel={
                    renewalStatus === 'LOAN_RENEWAL' && !isEmpty(previousLoans)
                      ? getLocalizedText(
                          'offer-page.termInputRadio.radioInfoMetricLabel1RenewalOpt2',
                        )
                      : getLocalizedText(
                          'offer-page.termInputRadio.radioInfoMetricLabel1Opt2',
                        )
                  }
                  labelType="caption"
                  value={
                    offerValues?.offerAmount &&
                    formattedCurrency(offerValues.offerAmount)
                  }
                  valueType="caption"
                />
                <Divider className="dividerV2" />
                <OfferInfoMetricsV2
                  infoLabel={getLocalizedText(
                    'offer-page.termInputRadio.radioInfoMetricLabel2Opt2',
                  )}
                  labelType="caption"
                  value={
                    offerValues?.financeCharges &&
                    '+' + formattedCurrency(offerValues.financeCharges)
                  }
                  valueType="caption"
                  toolTipText={getLocalizedText(
                    'offer-page.termInputRadio.radioInfoMetricToolTipOpt2',
                  )} //FIX:ME - Change the text once FIGMA is updated
                />
                <Divider className="dividerV2" />
                <OfferInfoMetricsV2
                  infoLabel={getLocalizedText(
                    'offer-page.termInputRadio.radioInfoMetricLabel3Opt2',
                  )}
                  labelType="caption"
                  value={
                    offerValues?.offerAmount &&
                    offerValues?.financeCharges &&
                    formattedCurrency(
                      offerValues.offerAmount + offerValues.financeCharges,
                    )
                  }
                  valueType="caption"
                />
              </div>
            </AccordionRow>
          </Accordion>
        ) : (
          <div className="disbledRadioButtonV2">
            <LockIcon
              className="lockIconV2"
              size="sm"
              color="sysColorBackgroundMediumContrast"
            />
            <BodyText className="lockTextV2 lockCaptionV2">
              {interpolate(
                getLocalizedText(
                  'offer-page.termInputRadio.radioLockedTextOpt2',
                ),
                {
                  loanTermLable,
                  limitAmount: <b>{`$${toCommaSeparatedNumber(loanLimit)}`}</b>,
                },
              )}
            </BodyText>
            <CaptionText className="lockTextMobileV2 lockCaptionV2">
              {interpolate(
                getLocalizedText(
                  'offer-page.termInputRadio.radioLockedTextOpt2',
                ),
                {
                  loanTermLable,
                  limitAmount: <b>{`$${toCommaSeparatedNumber(loanLimit)}`}</b>,
                },
              )}
            </CaptionText>
          </div>
        )}
      </Card>
    </div>
  )
}

export default TermInputRadioV2
